import type React from "react";
import { useMemo, useState } from "react";
import "./NoteCard.css";
import CheckIcon from "../../assets/svgs/note/checkIcon";
import CrossIcon from "../../assets/svgs/note/crossIcon";
import ActionMenu from "../actionMenus/action-menu";
import editIcon from "../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import { NotesEntityTypesEnum } from "@propertelligent/client-api";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import { FormProvider, useForm } from "react-hook-form";
import DescriptionTextarea from "../form-fields/DescriptionTextarea";
import SimpleSpinner from "../../components/loader";

interface NotesValues {
  formatted?: Record<string, any> | null;
  id?: number;
  entityTypeId?: NotesEntityTypesEnum;
  entityId?: number;
  noteData?: string | null;
  modified?: string;
  modifiedById?: number;
  modifiedBy?: string | null;
}

interface NoteCardProps {
  title: string;
  author: string;
  date: string;
  entityTypeId?: NotesEntityTypesEnum;
  entityId?: number;
  id?: number;
  onClose?: () => void;
  onUpdate?: (data: NotesValues) => void;
  onDelete?: (id: number) => void;
  isLoading?: boolean;
}

const NoteCard: React.FC<NoteCardProps> = ({
  entityId,
  entityTypeId,
  title,
  author,
  date,
  id,
  onClose,
  onUpdate,
  onDelete,
  isLoading = false,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  // const [isVisible, setIsVisible] = useState(true);
  const [deleteState, setDeleteState] = useState({
    id: null as number | null,
    isOpen: false,
  });

  const methods = useForm<NotesValues>({
    defaultValues: {
      id: id || null,
      entityTypeId: entityTypeId || NotesEntityTypesEnum.Property,
      entityId: entityId,
      noteData: title,
    },
  });

  const handleMenuClick = () => {
    setIsEditMode(!isEditMode);
    if (!isEditMode) {
      methods.reset({
        id: id || null,
        entityTypeId: entityTypeId || NotesEntityTypesEnum.Property,
        entityId: entityId,
        noteData: title,
      });
    }
  };

  const handleClose = () => {
    setIsEditMode(false);
    methods.reset();
  };

  const handleUpdate = async () => {
    const data = methods.getValues();
    if (onUpdate) {
      await onUpdate(data);
    }
    setIsEditMode(false);
  };

  const handleDelete = () => {
    if (!id) return;
    if (onDelete) onDelete(id);
    setDeleteState({ id: null, isOpen: false });
    // setIsVisible(false);
  };

  const MENU_ITEMS = [
    {
      icon: editIcon,
      label: "Edit",
      action: () => handleMenuClick(),
      isDivider: false,
      isDestructive: false,
    },
    { isDivider: true },
    {
      icon: deleteIcon,
      label: "Delete",
      action: () => setDeleteState({ id: id || null, isOpen: true }),
      isDivider: false,
      isDestructive: false,
    },
  ];

  const menuItems = useMemo(() => MENU_ITEMS, [id]);

  // if (!isVisible) return null;

  return (
    <div className="note-card">
      <div className="card-content">
        {isEditMode ? (
          <FormProvider {...methods}>
            <form id="NotesForm" onSubmit={methods.handleSubmit(handleUpdate)}>
              <div className="row">
                <div className="col-md-12">
                  <DescriptionTextarea
                    name="noteData"
                    className="card-title-input"
                    requiredMessage="Note is required"
                    rows={3}
                    required
                    placeholder="Enter Note"
                    disabled={isLoading}
                    validation={{
                      maxLength: {
                        value: 250,
                        message: "Note cannot be more than 250 characters"
                      }
                    }}
                  />
                </div>
              </div>
            </form>
          </FormProvider>
        ) : (
          <p className="card-title">{title}</p>
        )}

        <div className="card-footer">
          <div className="card-meta">
            <span className="author">{author}</span>
            <span className="separator">|</span>
            <span className="date">{date}</span>
          </div>
          <div className="card-actions">
            {isEditMode ? (
              <>
                <button
                  className="btn btn-close-custom"
                  onClick={handleClose}
                  aria-label="Cancel Edit"
                  disabled={isLoading}
                >
                  <CrossIcon width="35" height="34" />
                </button>
                <button
                  className="btn btn-approve"
                  onClick={() => methods.handleSubmit(handleUpdate)()}
                  aria-label="Save"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <SimpleSpinner myClassName="Icon16" />
                  ) : (
                    <CheckIcon width="35" height="34" />
                  )}
                </button>
              </>
            ) : null}
            {!isEditMode && (
              <ActionMenu
                menuItems={menuItems}
                trigger={
                  <button className="action-button">
                    <img
                      src={dropdownIcon}
                      alt="More options"
                      width={20}
                      height={20}
                      className="action-icon"
                    />
                  </button>
                }
              />
            )}
          </div>
        </div>
      </div>
      <ConfirmationPopup
        isOpen={deleteState.isOpen}
        title="Delete Note"
        message="Are you sure you want to delete this Note?"
        primaryButtonLabel="Delete"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleDelete}
        onSecondaryAction={() => setDeleteState({ id: null, isOpen: false })}
        onClose={() => setDeleteState({ id: null, isOpen: false })}
        isPrimaryDestructive={true}
        isLoading={isLoading}
        loadingLabel="Deleting..."
      />
    </div>
  );
};

export default NoteCard;